import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/** Divide il testo in battute di sottotitolo leggibili. */
const spezza = (testo) =>
  testo
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?:;])\s+/)
    .flatMap((f) => {
      const parole = f.trim().split(" ");
      if (parole.length <= 12) return f.trim() ? [f.trim()] : [];
      const out = [];
      for (let i = 0; i < parole.length; i += 10) out.push(parole.slice(i, i + 10).join(" "));
      return out;
    });

/**
 * Voce del PM-Bot: genera l'audio, lo riproduce e tiene i sottotitoli sincronizzati
 * sulla durata reale della traccia.
 */
export const useVoice = () => {
  const audioRef = useRef(null);
  const [disponibile, setDisponibile] = useState(false);
  const [voci, setVoci] = useState([]);
  const [voceId, setVoceId] = useState(() => localStorage.getItem("rg_voice") || "");
  const [muto, setMuto] = useState(false);
  const [inRiproduzione, setInRiproduzione] = useState(false);
  const [caricamento, setCaricamento] = useState(false);
  const [battute, setBattute] = useState([]);
  const [indice, setIndice] = useState(0);

  useEffect(() => {
    api
      .get("/voice/stato")
      .then(({ data }) => {
        setDisponibile(data.disponibile);
        setVoci(data.voci || []);
        setVoceId((v) => v || data.voice_id || "");
      })
      .catch(() => setDisponibile(false));
    const el = new Audio();
    audioRef.current = el;
    return () => {
      el.pause();
      el.src = "";
    };
  }, []);

  const scegliVoce = useCallback((id) => {
    setVoceId(id);
    localStorage.setItem("rg_voice", id);
  }, []);

  const stop = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setInRiproduzione(false);
    setIndice(0);
  }, []);

  const parla = useCallback(
    async (testo) => {
      const righe = spezza(testo || "");
      setBattute(righe);
      setIndice(0);
      if (!testo || muto || !disponibile) return;
      const el = audioRef.current;
      if (!el) return;
      setCaricamento(true);
      try {
        const { data } = await api.post("/voice/tts", { testo, voice_id: voceId || undefined });
        el.src = data.audio;
        el.onended = () => {
          setInRiproduzione(false);
          setIndice(righe.length - 1);
        };
        el.ontimeupdate = () => {
          if (!el.duration || !righe.length) return;
          const pos = Math.floor((el.currentTime / el.duration) * righe.length);
          setIndice(Math.min(pos, righe.length - 1));
        };
        await el.play();
        setInRiproduzione(true);
      } catch {
        setInRiproduzione(false);
      } finally {
        setCaricamento(false);
      }
    },
    [muto, disponibile, voceId]
  );

  const toggleRiproduzione = useCallback(async () => {
    const el = audioRef.current;
    if (!el || !el.src) return;
    if (el.paused) {
      await el.play();
      setInRiproduzione(true);
    } else {
      el.pause();
      setInRiproduzione(false);
    }
  }, []);

  const toggleMuto = useCallback(() => {
    setMuto((m) => {
      const nuovo = !m;
      const el = audioRef.current;
      if (el) el.muted = nuovo;
      if (nuovo && el) {
        el.pause();
        setInRiproduzione(false);
      }
      return nuovo;
    });
  }, []);

  return {
    disponibile,
    voci,
    voceId,
    scegliVoce,
    muto,
    inRiproduzione,
    caricamento,
    battute,
    indice,
    parla,
    stop,
    toggleRiproduzione,
    toggleMuto,
  };
};
